import { useMutation, useQuery } from "@apollo/client";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { GET_SALON_CONFIGURATIONS, UPDATE_SALON_CONFIGURATION } from "@/graphql/configuration";
import { useConfigurationSubscription } from "@/hooks/use-configuration-subscription";
import { useSalonId } from "@/lib/store/salon";
import { getConfigurationValue } from "@/lib/utils/configuration-utils";
import type { CapacityFormData } from "../types";

export function useCapacitySettings() {
  const salonId = useSalonId();

  // Real-time updates so changes from Partner/another tab show up immediately.
  useConfigurationSubscription({
    salonId: salonId ?? "",
    categories: ["appointment_limits"],
    enabled: !!salonId,
  });

  // GraphQL query for capacity configurations
  const {
    data: configData,
    loading: configLoading,
    error: configError,
    refetch,
  } = useQuery(GET_SALON_CONFIGURATIONS, {
    variables: {
      salonId,
      category: "appointment_limits",
    },
    skip: !salonId,
  });

  // GraphQL mutation for updating configurations
  const [updateConfiguration] = useMutation(UPDATE_SALON_CONFIGURATION);
  const [saving, setSaving] = useState(false);

  // Transform GraphQL data to form data
  const configs = configData?.getSalonConfigurations?.configurations || [];

  const values = {
    capacityMode: getConfigurationValue(configs, "appointment_limits.capacity_mode"),
    concurrentMax: getConfigurationValue(configs, "appointment_limits.concurrent_max"),
    dailyMax: getConfigurationValue(configs, "appointment_limits.daily_max"),
    durationAware: getConfigurationValue(configs, "appointment_limits.duration_aware"),
    concurrentInfinity: getConfigurationValue(configs, "appointment_limits.concurrent_infinity"),
    dailyInfinity: getConfigurationValue(configs, "appointment_limits.daily_infinity"),
  };

  // Create form. `keepDirtyValues` prevents the subscription/refetch from
  // wiping in-flight edits when the cache updates.
  const form = useForm<CapacityFormData>({
    values: values,
    resetOptions: { keepDirtyValues: true },
    mode: "onChange",
  });

  // Save function - updates multiple configurations
  const save = useCallback(async () => {
    if (!salonId) {
      toast.error("No salon selected");
      return;
    }

    try {
      const formData = form.getValues();

      // The daily limit applies in both modes. The concurrent limit belongs to
      // simple mode, so advanced mode keeps the stored one for switching back.
      const updates = [
        {
          key: "appointment_limits.capacity_mode",
          value: formData.capacityMode,
          dataType: "string",
        },
        ...(formData.capacityMode === "simple"
          ? [
              {
                key: "appointment_limits.concurrent_max",
                value: formData.concurrentMax.toString(),
                dataType: "int",
              },
            ]
          : []),
        {
          key: "appointment_limits.daily_max",
          value: formData.dailyMax.toString(),
          dataType: "int",
        },
        {
          key: "appointment_limits.duration_aware",
          value: String(formData.durationAware),
          dataType: "boolean",
        },
        // Note: concurrent_infinity and daily_infinity are deprecated
        // Values >= UNLIMITED_CAPACITY (999) are treated as infinity in the UI
      ];

      // Diff against the stored values instead of reading `form.formState.isDirty`,
      // which React Compiler can memoize away inside this callback.
      const stored: Record<string, string> = {
        "appointment_limits.capacity_mode": values.capacityMode,
        "appointment_limits.concurrent_max": values.concurrentMax.toString(),
        "appointment_limits.daily_max": values.dailyMax.toString(),
        "appointment_limits.duration_aware": String(values.durationAware),
      };
      const changed = updates.filter((update) => update.value !== stored[update.key]);
      if (changed.length === 0) {
        toast.info("No changes to save");
        return;
      }

      setSaving(true);
      await Promise.all(
        changed.map((update) =>
          updateConfiguration({
            variables: {
              salonId,
              input: update,
            },
          })
        )
      );

      // Refetch to ensure UI is up to date
      await refetch();

      toast.success("Capacity settings saved successfully!");
    } catch (error) {
      console.error("Capacity settings save error:", error);
      toast.error("Failed to save capacity settings");
      throw error;
    } finally {
      setSaving(false);
    }
  }, [
    form,
    salonId,
    updateConfiguration,
    refetch,
    values.capacityMode,
    values.concurrentMax,
    values.dailyMax,
    values.durationAware,
  ]);

  // Discard changes
  const discard = useCallback(() => {
    form.reset();
    toast.info("Changes discarded");
  }, [form]);

  // Handle mode change through form
  const handleModeChange = useCallback(
    (mode: "simple" | "advanced") => {
      form.setValue("capacityMode", mode, { shouldDirty: true });
      // Advanced mode does not save the concurrent limit, so drop unsaved edits to
      // it; otherwise it stays dirty after Save and is lost silently.
      // `shouldDirty` re-compares with the stored value, so the field ends up clean.
      if (mode === "advanced") {
        form.setValue("concurrentMax", values.concurrentMax, { shouldDirty: true });
      }
    },
    [form, values.concurrentMax]
  );

  return {
    form,
    loading: configLoading,
    saving,
    save,
    discard,
    handleModeChange,
    error: configError,
  };
}
